import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../axios";
import { canUseAnalysis, canUseFavoritesToday } from "../utils/analysisAccess";
import "../styles/analysis.css";

const DAY_TYPE_LABEL = { weekday: "平日", sat: "土曜", holiday: "日祝" };

const CONFIDENCE_CLASS = {
  高: "anl-badge--high",
  中: "anl-badge--mid",
  低: "anl-badge--low",
  不明: "anl-badge--unknown",
};

/**
 * 並べる順。**走っている車をいちばん上に。**
 *
 * 「今日はまだ」「そのダイヤはまだ」を上に置くと、開いた瞬間に
 * 出せないものばかりが目に入る。
 */
const STATE_ORDER = { running: 0, unknown: 1, finished: 2, idle: 3, unavailable: 4 };

// 解析の対象外。カードにしても言うことが無いので並べない
const HIDDEN_STATES = new Set(["unsupported", "retired"]);

/**
 * お気に入り車両の「今日」。
 *
 * 8 日間のマトリクスは**あとから見る**ための表で、いま追いかけるには向かない。
 * 自分の推し車両が、今日どの乗番に入っていて、次にどこへ現れるかだけを上に出す。
 *
 * ## 状態を混ぜない
 *
 * 「今日はまだ目撃がない」「目撃はあるが乗番が特定できない」「そのダイヤ種別の
 * 乗番をまだ組めていない」はまったく違う。同じ「—」で片付けると、動いていない
 * のか、こちらが出せていないのかが読み手に分からない。API が state で分けて
 * 返すので、文言もそれぞれ変える。
 *
 * ## 予測は予測として出す
 *
 * これからの便は紫の点線。**目撃ではない。**乗番から「この先ここを走るはず」と
 * 言っているだけで、車両交代が起きればそのとおりにはならない。
 */
export default function FavoriteDutyToday({ currentUser }) {
  // **当面は super_admin だけに出す**（utils/analysisAccess.js）。
  // 見せない相手には API も叩かない
  const visible = canUseFavoritesToday(currentUser);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fetchToday = useCallback(() => {
    if (!visible) return;
    axios
      .get("/user/favorites/today")
      .then((res) => {
        setData(res.data);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    fetchToday();
    // サーバー側も 3 分貯めている。それより短く叩いても同じ答えしか返らない
    const timer = setInterval(fetchToday, 180000);
    return () => clearInterval(timer);
  }, [fetchToday, visible]);

  if (!visible || loading || failed || !data) return null;

  const buses = data.buses
    .filter((b) => !HIDDEN_STATES.has(b.state))
    .sort(
      (a, b) =>
        (STATE_ORDER[a.state] ?? 9) - (STATE_ORDER[b.state] ?? 9) ||
        (a.next?.departure_time ?? "").localeCompare(b.next?.departure_time ?? "") ||
        a.bus_number.localeCompare(b.bus_number)
    );

  // 解析している営業所の車を 1 台もお気に入りにしていなければ、何も出さない
  if (buses.length === 0) return null;

  return (
    <section className="fav-today">
      <div className="fav-today__head">
        <span className="fav-today__title">今日の運用</span>
        <span className="fav-today__meta">
          {data.service_date}（{DAY_TYPE_LABEL[data.day_type] ?? data.day_type}ダイヤ）
          <span className="fav-today__at">{data.now.slice(11)}時点</span>
        </span>
      </div>

      <div className="fav-today__list">
        {buses.map((bus) => (
          <BusCard key={bus.bus_number} bus={bus} currentUser={currentUser} />
        ))}
      </div>

      <p className="fav-today__legend">
        <span className="fav-today__forecast-mark" aria-hidden="true" />
        紫の点線はこれから走る<strong>予定</strong>。目撃ではありません。
        途中で車両が変わればこのとおりにはなりません。
      </p>
    </section>
  );
}

function BusCard({ bus, currentUser }) {
  const linkable = canUseAnalysis(currentUser);

  return (
    <div className={`fav-today__card fav-today__card--${bus.state}`}>
      <div className="fav-today__card-head">
        <span className="fav-today__bus">
          {linkable ? (
            <Link className="anl-link" to={`/analysis/buses/${bus.bus_number}`}>
              {bus.bus_number}
            </Link>
          ) : (
            bus.bus_number
          )}
        </span>
        {bus.duty && (
          <>
            <span className="anl-badge anl-badge--code">{bus.duty.code}</span>
            <span className={`anl-badge ${CONFIDENCE_CLASS[bus.confidence_label] ?? ""}`}>
              確度{bus.confidence_label}
            </span>
          </>
        )}
      </div>

      <Body bus={bus} />
    </div>
  );
}

function Body({ bus }) {
  if (bus.state === "unavailable") {
    // **「特定できなかった」ではない。**そのダイヤ種別の乗番をまだ組めていない
    return <p className="fav-today__note">このダイヤの乗番はまだ集計できていません。</p>;
  }

  if (bus.state === "idle") {
    return <p className="fav-today__note">今日はまだ目撃がありません。</p>;
  }

  if (bus.state === "unknown") {
    return (
      <p className="fav-today__note">
        {bus.observed_trips}便を目撃。<strong>乗番はまだ分かりません。</strong>
        <br />
        もう1便どこかで見つかれば決まることがあります。
      </p>
    );
  }

  return (
    <>
      <div className="fav-today__stat">
        一致{bus.matched_trips} / {bus.duty.trip_count}便
        {bus.duty_count > 1 && (
          /* 1 日に 2 つ以上の乗番をこなす日がある（§14）。
             総数だけだと「いまどれを走っているのか」が読めない */
          <span className="fav-today__multi">
            本日{bus.duty_count}乗番のうち{bus.duty_seq}本目
          </span>
        )}
      </div>

      {bus.last && (
        <div className="fav-today__trip fav-today__trip--done">
          <span className="fav-today__time">{bus.last.departure_time}</span>
          <span className="fav-today__where">
            {bus.last.route} {bus.last.start}発
          </span>
          <span className="fav-today__tag">直前</span>
        </div>
      )}

      {bus.state === "finished" ? (
        <p className="fav-today__note">
          この乗番はこれで終わりです（終発{bus.duty.last_departure}）。
        </p>
      ) : (
        <>
          {bus.upcoming.map((trip) => (
            <div key={trip.trip_id} className="fav-today__trip fav-today__trip--forecast">
              <span className="fav-today__time">{trip.departure_time}</span>
              <span className="fav-today__where">
                {trip.route} {trip.start}発
              </span>
            </div>
          ))}
          {bus.remaining > bus.upcoming.length && (
            <p className="fav-today__more">ほか{bus.remaining - bus.upcoming.length}便</p>
          )}
        </>
      )}
    </>
  );
}
